<script lang="ts" setup>
import { config } from '@layouts/config'
import { isNavItemVisible } from '@/composables/useNavItemVisibility'
import type { navItem } from '@/navigation/vertical'
import type { NavRailVariant } from '@/composables/useNavGridLayout'

interface Props {
  items: navItem[]
  size: number
  // DEVAMS-2670 second-round review: 'compact' (tablet) vs 'expanded' (web,
  // wider mini-drawer with icon+label rows). Phone no longer renders this
  // component at all — its favorites live in Footer.vue's bottom tab bar.
  // DEVAMS-2670 fifth-round review: both variants render the toggle button
  // as the first item at the top of the rail, above the favorites.
  variant: NavRailVariant
  showToggle: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'open-nav'): void
}>()
</script>

<template>
  <div
    class="nav-favorites-rail nav-favorites-rail--vertical"
    :class="[`nav-favorites-rail--${props.variant}`]"
    :style="{ inlineSize: `${props.size}px` }"
  >
    <button
      v-if="props.showToggle"
      type="button"
      class="nav-favorites-rail-toggle"
      title="Open full menu"
      aria-label="Open full menu"
      @click="emit('open-nav')"
    >
      <VIcon
        icon="mdi-menu"
        class="nav-favorites-rail-item-icon"
      />
      <span
        v-if="props.variant === 'expanded'"
        class="nav-favorites-rail-item-label"
      >Full menu</span>
    </button>

    <span
      v-if="props.showToggle"
      class="nav-favorites-rail-section-label text-caption text-medium-emphasis"
    >Favorites</span>

    <div class="nav-favorites-rail-items">
      <template v-for="(item, index) in props.items">
        <RouterLink
          v-if="isNavItemVisible(item)"
          :key="'rail_' + index"
          :to="item.to"
          class="nav-favorites-rail-item"
          :title="item.title"
        >
          <span
            v-if="item.badgeContent"
            class="nav-favorites-rail-item-badge"
            :class="item.badgeClass"
          >
            {{ item.badgeContent }}
          </span>
          <Component
            :is="config.app.iconRenderer || 'div'"
            v-if="item.icon"
            v-bind="item.icon"
            class="nav-favorites-rail-item-icon"
          />
          <span class="nav-favorites-rail-item-label">{{ item.title }}</span>
        </RouterLink>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use "@configured-variables" as variables;

// DEVAMS-2670: always-visible favorites rail, rendered OUTSIDE the
// overlay-nav drawer's hide/show transform so it never disappears. Sits
// below both the full-nav drawer and its dimming scrim (both z-index 11/12)
// so opening the full nav visually dims the rail along with the rest of the
// page content — intentional, matches the scrim's "everything but the
// drawer is backgrounded" behavior.
//
// Second-round review: only ever rendered as a vertical left-edge rail now
// (phone dropped the rail entirely in favor of Footer.vue's bottom tab
// bar), split into 'compact' (tablet) vs 'expanded' (web) variants.
.nav-favorites-rail {
  position: fixed;
  z-index: variables.$layout-vertical-nav-layout-navbar-z-index - 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgb(var(--v-theme-surface));
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  inset-block: 0;
  inset-inline-start: 0;
  block-size: 100%;
  border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  gap: 0.5rem;
  padding-block-start: calc(0.75rem + var(--safe-area-inset-top, 0px));
  padding-block-end: 0.75rem;
}

.nav-favorites-rail--expanded {
  align-items: stretch;
  padding-inline: 0.5rem;
}

.nav-favorites-rail-toggle {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  inline-size: 2.5rem;
  block-size: 2.5rem;
  padding: 0;
  color: inherit;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  margin-block-end: 0.25rem;
  font: inherit;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }
}

// DEVAMS-2670 fifth-round review: web's toggle button matches the row
// layout of the "folded out" mini-drawer items below it (icon + label side
// by side, full width) instead of the tablet's small centered square.
.nav-favorites-rail--expanded .nav-favorites-rail-toggle {
  flex-direction: row;
  justify-content: flex-start;
  inline-size: 100%;
  block-size: auto;
  gap: 0.75rem;
  padding-block: 0.5rem;
  padding-inline: 0.75rem;
}

.nav-favorites-rail--expanded .nav-favorites-rail-toggle .nav-favorites-rail-item-label {
  font-size: 0.8125rem;
  text-align: start;
}

// DEVAMS-2670: dense-rail-friendly section label separating the toggle from
// the favorites list below it. Rendered for every rail-bearing tier
// (compact and expanded both show text labels under favorite-item icons —
// see .nav-favorites-rail-item-label — so both get this divider too).
.nav-favorites-rail-section-label {
  inline-size: 100%;
  padding-block: 0.375rem;
  padding-inline: 0.375rem;
  margin-block-start: 0.25rem;
  overflow: hidden;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  text-overflow: ellipsis;
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.nav-favorites-rail--expanded .nav-favorites-rail-section-label {
  padding-inline: 0.75rem;
  text-align: start;
}

.nav-favorites-rail-items {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  overflow-x: hidden;
  overflow-y: auto;
}

.nav-favorites-rail--expanded .nav-favorites-rail-items {
  align-items: stretch;
}

.nav-favorites-rail-item {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  padding: 0.375rem;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
  font: inherit;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }

  &.router-link-active {
    color: rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-primary), 0.12);
  }
}

// DEVAMS-2670 third-round review: widened from 2.75rem (44px) to fit the
// wider 108px compact rail (see useNavGridLayout.ts tabletPortrait/
// tabletLandscape railSize) and to give labels room to wrap onto two lines
// instead of being ellipsis-truncated.
.nav-favorites-rail--compact .nav-favorites-rail-item {
  inline-size: 100%;
  block-size: auto;
  min-block-size: 2.75rem;
  padding-block: 0.375rem;
}

// DEVAMS-2670: web's "folded out" mini-drawer — icon and label sit side by
// side per row instead of stacked, full rail width, more breathing room.
.nav-favorites-rail--expanded .nav-favorites-rail-item {
  flex-direction: row;
  justify-content: flex-start;
  inline-size: 100%;
  gap: 0.75rem;
  padding-block: 0.5rem;
  padding-inline: 0.75rem;
}

.nav-favorites-rail-item-icon {
  font-size: 1.375rem;
}

.nav-favorites-rail--expanded .nav-favorites-rail-item-icon {
  flex-shrink: 0;
}

.nav-favorites-rail-item-label {
  inline-size: 100%;
  overflow: hidden;
  font-size: 0.6875rem;
  line-height: 1.1;
  text-align: center;
  // DEVAMS-2670 third-round review: wrap onto two lines instead of
  // ellipsis-truncating (was cutting "Dashboard" -> "Das…", "Your roster" ->
  // "You…" at the old 80px rail width). Font size intentionally left
  // unchanged — already at the legibility floor.
  white-space: normal;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.nav-favorites-rail--expanded .nav-favorites-rail-item-label {
  font-size: 0.8125rem;
  text-align: start;
}

.nav-favorites-rail-item-badge {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  min-inline-size: 1.125rem;
  padding: 0 0.25rem;
  font-size: 0.625rem;
  line-height: 1.125rem;
  text-align: center;
  color: rgb(var(--v-theme-on-error));
  background-color: rgb(var(--v-theme-error));
  border-radius: 999px;
}

.nav-favorites-rail--expanded .nav-favorites-rail-item-badge {
  position: static;
  margin-inline-start: auto;
}
</style>
